import { FunctionComponent, useEffect, useState } from "react";
import classnames from "classnames";
import styles from "./SecondaryLabelCarousel.web.css";

const INITIAL_DELAY = 1000;
const FADE_DURATION = 300;
const VISIBLE_DURATION = 2000;

interface SecondaryLabelCarouselProps {
  messages: string | string[];
}

enum Phase {
  InitialDelay,
  FadeIn,
  Visible,
  FadeOut,
}

/**
 * Cycles through brand-specific progress messages beneath the primary label.
 *
 * Timeline:
 * - Initial delay (1000 ms)
 * - Fade in (300 ms)
 * - Visible (2000 ms)
 * - Fade out (300 ms), then the next message fades in
 * - Repeat until the final message, which remains visible.
 */
export const SecondaryLabelCarousel: FunctionComponent<SecondaryLabelCarouselProps> = ({ messages }) => {
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState(Phase.InitialDelay);

  const hasNextMessage = index < messages.length - 1;

  useEffect(() => {
    if (messages.length === 0) {
      return;
    }

    let timeout: ReturnType<typeof setTimeout>;

    switch (phase) {
      case Phase.InitialDelay:
        timeout = setTimeout(() => setPhase(Phase.FadeIn), INITIAL_DELAY);
        break;

      case Phase.FadeIn:
        timeout = setTimeout(() => setPhase(Phase.Visible), FADE_DURATION);
        break;

      case Phase.Visible:
        if (!hasNextMessage) {
          return;
        }

        timeout = setTimeout(() => setPhase(Phase.FadeOut), VISIBLE_DURATION);
        break;

      case Phase.FadeOut:
        timeout = setTimeout(() => {
          setIndex((current) => Math.min(current + 1, messages.length - 1));
          setPhase(Phase.FadeIn);
        }, FADE_DURATION);
        break;
    }

    return () => clearTimeout(timeout);
  }, [phase, hasNextMessage, messages.length]);

  if (messages.length === 0 || phase === Phase.InitialDelay) {
    return null;
  }

  return (
    <span
      className={classnames(styles.secondaryLabel, styles.carousel, {
        [styles.carouselFadeIn]: phase === Phase.FadeIn,
        [styles.carouselVisible]: phase === Phase.Visible,
        [styles.carouselFadeOut]: phase === Phase.FadeOut,
      })}
    >
      {messages[index]}
    </span>
  );
};
